/* eslint-disable react/jsx-props-no-spreading */
import fhirpath from 'fhirpath';
import React from 'react';

import Information from '@/components/Information';

import { getSolicitacaoObrigatoriaMotivo } from '@/features/recursos/utils/solicitacaoObrigatoria';
import useEllipsis from '@/utils/hooks/useExpandableRef';
import OptionIntegracao from '@/types/OptionIntegracao';
import SolicitacoesAPI from '@/features/prescricao-livre/services/SolicitacoesAPI';

import FormProcedimento from '../FormProcedimento';
import {
  Actions,
  BoxContent,
  Container,
  EditButton,
  EditedLabel,
  EditTools,
  ExpandButton,
  Header,
  HeaderSubTitle,
  HeaderTitle,
  HeaderSection,
} from './styles';

interface BoxProcedimentoEditavelProps {
  option: OptionIntegracao;
  detailsOpened?: boolean;
  disabled: boolean;
  onBeforeEdit?: () => void;
  onAfterEdit?: () => void;
  onAfterEditCancel?: () => void;
}

type ProcedureRequest = fhir4.ServiceRequest & {
  reportedBoolean: boolean;
};

const BoxProcedimentoEditavel: React.FC<BoxProcedimentoEditavelProps> = ({
  option,
  detailsOpened,
  disabled,
  onBeforeEdit,
  onAfterEdit,
  onAfterEditCancel,
}) => {
  const [isOpenForEdit, setIsOpenForEdit] = React.useState(
    detailsOpened || false,
  );
  const [original, setOriginal] = React.useState<ProcedureRequest>(
    option.pedido as ProcedureRequest,
  );
  const [draft, setDraft] = React.useState<ProcedureRequest>({
    ...(option.pedido as ProcedureRequest),
  });

  const [isSubtitleExpanded, setIsSubtitleExpanded] = React.useState(false);
  const [loadingConfirmation, setloadingConfirmation] = React.useState(false);
  const solicitacaoObrigatoriaMotivo = React.useMemo(
    () =>
      getSolicitacaoObrigatoriaMotivo(
        'procedimento',
        option.solicitacao_obrigatoria_motivo,
      ),
    [option.solicitacao_obrigatoria_motivo],
  );

  const nome = React.useMemo(() => {
    return fhirpath.evaluate(
      draft,
      'ServiceRequest.code.text | ServiceRequest.code.coding.display',
    )[0];
  }, [draft]);

  const comentario = React.useMemo(() => {
    const note = fhirpath.evaluate(draft, 'ServiceRequest.note.text');
    if (note.length) {
      return note[0];
    }

    return '';
  }, [draft]);

  const [subtitleRef, subtitileHasEllipis] = useEllipsis<HTMLHeadingElement>([
    comentario,
  ]);

  const edited = React.useMemo(() => !!draft.reportedBoolean, [
    draft.reportedBoolean,
  ]);

  const canExpand = React.useMemo(() => {
    // Se desabilitado, pode expandir e abrir formulário
    if (disabled) {
      return true;
    }
    return isSubtitleExpanded || subtitileHasEllipis;
  }, [disabled, isSubtitleExpanded, subtitileHasEllipis]);

  const canEdit = React.useMemo(() => !(disabled || isOpenForEdit), [
    disabled,
    isOpenForEdit,
  ]);

  const onEditClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      event.stopPropagation();

      setIsOpenForEdit(true);

      if (onBeforeEdit) {
        onBeforeEdit();
      }
    },
    [onBeforeEdit],
  );

  const toggleCollapse = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      // If disabled, collapse should display the edit form
      if (disabled) {
        setIsOpenForEdit(prev => !prev);
      }

      setIsSubtitleExpanded(prev => !prev);
    },
    [disabled],
  );

  const saveProcedimento = React.useCallback(
    async (item: fhir4.FhirResource) => {
      try {
        setloadingConfirmation(true);
        const result = await SolicitacoesAPI.update({
          id: option.value.toString(),
          resource: item,
          tipo: 'procedimento',
          opts: { session_id: option.sessionId },
        });

        if (result) {
          setIsOpenForEdit(false);
          setIsSubtitleExpanded(false);
          setDraft(result.pedido as ProcedureRequest);
          setOriginal(result.pedido as ProcedureRequest);
        }
      } finally {
        setloadingConfirmation(false);
        if (onAfterEdit) {
          onAfterEdit();
        }
      }
    },
    [onAfterEdit, option.sessionId, option.value],
  );

  const onEditCancel = React.useCallback(async () => {
    setDraft(original);

    setIsOpenForEdit(false);
    setIsSubtitleExpanded(false);

    if (onAfterEditCancel) {
      onAfterEditCancel();
    }
  }, [onAfterEditCancel, original]);

  return (
    <Container>
      <BoxContent>
        <HeaderSection>
          <Header>
            <HeaderTitle highlight={isOpenForEdit}>{nome}</HeaderTitle>

            <Actions>
              <EditTools>
                {edited && <EditedLabel>editado</EditedLabel>}
                {canEdit && (
                  <EditButton onClick={onEditClick} disabled={isOpenForEdit} />
                )}
              </EditTools>

              <ExpandButton
                disabled={!canExpand}
                collapsed={isSubtitleExpanded}
                onClick={toggleCollapse}
              />
            </Actions>
          </Header>

          {comentario && (
            <HeaderSubTitle ref={subtitleRef} highlight={isSubtitleExpanded}>
              {comentario}
            </HeaderSubTitle>
          )}

          {option.solicitacao_obrigatoria && (
            <Information type="warning" style={{ margin: '12px 0 0' }}>
              {solicitacaoObrigatoriaMotivo}
            </Information>
          )}
        </HeaderSection>

        {isOpenForEdit && (
          <FormProcedimento
            resource={draft}
            onSubmit={saveProcedimento}
            onCancel={onEditCancel}
            loading={loadingConfirmation}
            readOnly={disabled}
          />
        )}
      </BoxContent>
    </Container>
  );
};

export default BoxProcedimentoEditavel;
